import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { get } from 'lodash';
import { unwrapResult } from '@reduxjs/toolkit';

import { Capability } from '__types__';
import { useRestrictedAccess } from 'lib/restrictedAccess';
import {
  deleteBooking,
  unarchiveBooking
} from 'store/v1/bookings/bookings.actions.js';
import {
  closeBookingSidebar,
  displayAlert,
  openModal
} from 'store/v1/ui/ui.actions.js';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from 'v5/design-system/DropdownMenu';
import { Button } from 'v5/design-system/Button';
import { MoreHorizontal, Trash2 } from 'lucide-react';

const BookingOptions = ({ bookingId, appearance }) => {
  const dispatch = useDispatch();
  const bookings = useSelector(state => state.bookings.data);
  const booking = get(bookings, bookingId);

  const showMenu = useRestrictedAccess(null, null, [
    Capability.BOOKING_UPDATE,
    Capability.BOOKING_ARCHIVE
  ]);

  const hasDeletePermission = useRestrictedAccess(null, null, [
    Capability.BOOKING_ARCHIVE
  ]);

  if (!showMenu || !booking) {
    return null;
  }

  const onArchive = () => {
    dispatch(
      openModal('ConfirmModal', {
        title: 'Are you sure you want to archive this booking?',
        description:
          'You will not lose any data but you will no longer see this booking within the list',
        confirmLabel: 'Archive booking',
        onConfirm: () => {
          dispatch(deleteBooking(bookingId))
            .then(unwrapResult)
            .then(() => {
              dispatch(closeBookingSidebar());
              dispatch(displayAlert('success', 'Booking archived'));
            })
            .catch(() => {
              dispatch(displayAlert('error', 'Something went wrong'));
            });
        }
      })
    );
  };

  const onUnarchive = () => {
    dispatch(
      openModal('ConfirmModal', {
        title: 'Are you sure you want to unarchive this booking?',
        description:
          'This booking will reappear within the booking list as well as within the resources calendar',
        confirmLabel: 'Unarchive booking',
        onConfirm: () => {
          dispatch(unarchiveBooking(bookingId))
            .then(unwrapResult)
            .then(() => {
              dispatch(displayAlert('success', 'Booking unarchived'));
            })
            .catch(() => {
              dispatch(displayAlert('error', 'Something went wrong'));
            });
        }
      })
    );
  };

  return (
    <div className="tailwind-root contents">
      <DropdownMenu icon="ellipsis" appearance={appearance}>
        <DropdownMenuTrigger asChild>
          <Button variant="secondary" size="sm" layout="icon">
            <MoreHorizontal />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {booking && !booking.archived && (
            <DropdownMenuItem onSelect={onArchive}>
              <Trash2 className="text-fg-danger" />
              Archive booking
            </DropdownMenuItem>
          )}
          {hasDeletePermission && booking && booking.archived && (
            <DropdownMenuItem onSelect={onUnarchive}>
              <img src="/images/icon_unarchive.svg" alt="Unarchive booking" />
              Unarchive booking
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

BookingOptions.propTypes = {
  bookingId: PropTypes.number,
  appearance: PropTypes.string // From PressStud
};

export default BookingOptions;
